import { Flex, useMantineTheme } from '@mantine/core';
import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionProps, SingleValueProps } from 'react-select-modern';
import { components } from 'react-select-modern';

import type { ITargetable } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import { Button, Pill, Text, UniversalSelect } from '@feathr/components';
import { useModals } from '@feathr/extender/components';

import GeoFilterModal from '../GeoFilterModal';
import GeoFiltersList from '../GeoFiltersList';

import * as styles from './GeoFilterSet.css';

interface IProps {
  onEditGeoFilterSet?: (geoFilterSet: ITargetable) => void;
}

interface IGeoFilterSetOption
  extends Pick<ITargetable, 'included_geo_filters' | 'excluded_geo_filters'> {
  id: string;
  _cls: TargetableClass.geo_set;
  name: string;
  is_default?: boolean;
}

// Mock data for geofilter sets
const MOCK_GEO_FILTER_SETS: IGeoFilterSetOption[] = [
  {
    id: 'geofilter-set-a',
    _cls: TargetableClass.geo_set,
    name: 'US Major Cities',
    included_geo_filters: ['582e5166bb33571f205b5663', '582e539fbb33571f205b6703'],
    excluded_geo_filters: ['582e4e49bb33571f205b3f9e'],
    is_default: false,
  },
  {
    id: 'geofilter-set-b',
    _cls: TargetableClass.geo_set,
    name: 'European Capitals',
    included_geo_filters: ['582e2148bb33571c737e4129', '582e44a5bb33571c737f2805'],
    excluded_geo_filters: ['582e299dbb33571c737e7749'],
    is_default: true,
  },
  {
    id: 'geofilter-set-c',
    _cls: TargetableClass.geo_set,
    name: 'Florida Cities',
    included_geo_filters: ['582e4b05bb33571f205b27a3', '582e4b1cbb33571f205b285f'],
    excluded_geo_filters: ['582e4b2dbb33571f205b28c9'],
    is_default: false,
  },
];

function renderOptionOrSingleValue(
  props: SingleValueProps<IGeoFilterSetOption> | OptionProps<IGeoFilterSetOption>,
  t: TFunction,
): JSX.Element {
  const { data } = props;

  return (
    <Flex align={'center'} justify={'space-between'}>
      <span>{data.name}</span>
      {data.is_default && <Pill theme={'slate'}>{t('Default')}</Pill>}
    </Flex>
  );
}

function CustomSingleValue(
  props: SingleValueProps<IGeoFilterSetOption>,
  t: TFunction,
): JSX.Element {
  return (
    <components.SingleValue {...props}>
      {renderOptionOrSingleValue(props, t)}
    </components.SingleValue>
  );
}

function CustomOption(props: OptionProps<IGeoFilterSetOption>, t: TFunction): JSX.Element {
  return <components.Option {...props}>{renderOptionOrSingleValue(props, t)}</components.Option>;
}

function GeoFilterSet({ onEditGeoFilterSet }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [selectedGeoFilterSet, setSelectedGeoFilterSet] = useState<IGeoFilterSetOption | null>(
    null,
  );

  const { editCreate } = useModals();
  const theme = useMantineTheme();

  const handleClearSelection = useCallback((): void => {
    setSelectedGeoFilterSet(null);
  }, []);

  const handleSetSelection = useCallback((geoFilterSet: IGeoFilterSetOption | undefined): void => {
    const selectedSet = geoFilterSet ?? null;
    setSelectedGeoFilterSet(selectedSet);
  }, []);

  const handleEditGeoFilterSet = useCallback((): void => {
    // Also call the parent callback if provided - will be updated when we integrate
    if (selectedGeoFilterSet && onEditGeoFilterSet) {
      const targetable: ITargetable = {
        ...selectedGeoFilterSet,
        units: 'kilometers',
        radius: 0,
        type: 'Targetable',
      };
      onEditGeoFilterSet(targetable);
    }
  }, [selectedGeoFilterSet, onEditGeoFilterSet]);

  // Sort options to put default first
  const sortedGeoFilterSets = [...MOCK_GEO_FILTER_SETS].sort((a, b) => {
    // TODO: Replace with actual default geofilter set logic: https://github.com/Feathr/shrike/issues/6737
    if (a.is_default && !b.is_default) {
      return -1;
    }
    if (!a.is_default && b.is_default) {
      return 1;
    }
    return a.name.localeCompare(b.name);
  });

  const getOptionLabel = useCallback((option: IGeoFilterSetOption): string => {
    return option.name;
  }, []);

  return (
    <Flex direction={'column'} gap={theme.spacing[4]}>
      <Flex direction={'column'} gap={theme.spacing[2]}>
        <Text.DefaultMedium>{t('Geofilter set')}</Text.DefaultMedium>
        <Flex align={'center'} className={styles.dropdownWrapper} gap={theme.spacing[2]}>
          <UniversalSelect<IGeoFilterSetOption>
            components={{
              Option: (props) => CustomOption(props, t),
              SingleValue: (props) => CustomSingleValue(props, t),
            }}
            getOptionLabel={getOptionLabel}
            isClearable={true}
            isFullWidth={true}
            name={'geofilter-set-selection'}
            onClear={handleClearSelection}
            onSelectSingle={handleSetSelection}
            options={sortedGeoFilterSets}
            placeholder={t('Search or select a geofilter set')}
            value={selectedGeoFilterSet}
          />
          {selectedGeoFilterSet && (
            <Button onClick={editCreate.handleOpen('edit')} type={'secondary'}>
              {t('Edit geofilter set')}
            </Button>
          )}
        </Flex>
      </Flex>
      {selectedGeoFilterSet && (
        <GeoFiltersList
          excludedGeoFilterIds={selectedGeoFilterSet.excluded_geo_filters ?? []}
          includedGeoFilterIds={selectedGeoFilterSet.included_geo_filters ?? []}
        />
      )}

      <GeoFilterModal
        context={'edit'}
        onClose={editCreate.close}
        onSaveChanges={handleEditGeoFilterSet}
        opened={editCreate.isOpen}
      />
    </Flex>
  );
}

export default observer(GeoFilterSet);
